import React from 'react'
import { useLocation } from 'react-router-dom'
import Navbar from '@/shared/components/Header/Navbar'
import AlphaIcon from '../components/AlphaIcon'
import { useFeatureToggle } from '../hooks/useFeatureToggle'
import { CoachMarksRegistryProvider } from '@/shared/contexts/CoachMarksRegistryContext'

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isEnabled } = useFeatureToggle();
  const isAlphaEnabled = isEnabled('alpha');
  const location = useLocation();

  // When the user clicks a menu item for the page they're already on,
  // NavMenu sets location.state._mountKey to a unique timestamp.
  // Using it as the key on <main> forces a full remount of all page
  // components, resetting every piece of local state (filters, sort,
  // pagination, etc.) exactly as if the user navigated there fresh.
  const mountKey = (location.state as { _mountKey?: number } | null)?._mountKey;

  return (
    // `overflow-x-hidden` clips any wider chrome (e.g. the Navbar's flex row,
    // which has fixed-size children that don't shrink below ~1150 px) to the
    // viewport so child pages aren't pushed off-screen at narrow widths. The
    // dashboard's react-grid-layout owns its own width via a ResizeObserver
    // and will respond correctly to whatever room it's actually given.
    //
    // The `CoachMarksRegistryProvider` wraps both the Navbar (which renders
    // the global Tour button) and the routed page (which registers its
    // start function via `useRegisterTour`). Mounting it here keeps the
    // registry available across every page render without each one
    // setting it up locally.
    <CoachMarksRegistryProvider>
    <div className="flex h-screen w-full min-w-0 flex-col overflow-x-hidden bg-background">
      {/* Skip to main content — always available to keyboard users (WCAG 2.4.1) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-1/2 focus:-translate-x-1/2 focus:z-[220] focus:bg-primary focus:text-on-primary focus:px-5 focus:py-2 focus:rounded-md focus:shadow-lg focus:text-sm focus:font-medium focus:underline focus:underline-offset-2 focus:outline-none"
      >
        Skip to main content
      </a>
      <Navbar />
      {/* `min-w-0` + `overflow-x-hidden` belt-and-braces: prevents any
          page content from pushing <main> wider than the viewport at narrow
          widths (Mac split-screen 25/75, etc.). The dashboard's react-grid
          measures `containerRef`'s clientWidth via ResizeObserver, so once
          <main> stops growing past the viewport, the grid resizes correctly.
       */}
      <main
        key={mountKey}
        id="main-content"
        className="flex-1 flex flex-col min-h-0 min-w-0 overflow-x-hidden overflow-y-auto"
        tabIndex={-1}
      >
        {children}
      </main>
      {isAlphaEnabled && <AlphaIcon />}
    </div>
    </CoachMarksRegistryProvider>
  );
};
